/**
 * App State Engine - Reactive, schema-declared state management for app runtime.
 *
 * Three layers:
 * - Global: shared across all pages (auth status, user prefs, notifications)
 * - Page: scoped to a single page (form data, filters, selections)
 * - Component: local to a component (open/closed, hover, focus)
 *
 * All state is:
 * - Explicitly declared in schema
 * - Observable via subscriptions
 * - Serializable for snapshots and undo
 * - Resettable per scope
 *
 * No hidden mutation. No magic bindings.
 *
 * Extends: eventBus, immutableHistory
 */

import { eventBus } from "@/runtime/eventBus";
import { logger } from "@/system/logger";

// ─── State Types ───

export type StateScope = "global" | "page" | "component";

export interface StateDefinition {
  id: string;
  scope: StateScope;
  /** Owning page ID (for page scope) or component ID (for component scope) */
  ownerId?: string;
  /** Initial value - must be serializable */
  initialValue: StateValue;
  /** Type hint for validation */
  type: "string" | "number" | "boolean" | "array" | "object" | "null";
  /** Human-readable label */
  label?: string;
  /** Whether this state persists across sessions */
  persistent?: boolean;
}

export type StateValue =
  | string
  | number
  | boolean
  | null
  | StateValue[]
  | { [key: string]: StateValue };

export interface StateSnapshot {
  global: Record<string, StateValue>;
  pages: Record<string, Record<string, StateValue>>;
  components: Record<string, Record<string, StateValue>>;
  capturedAt: number;
}

type StateListener = (key: string, value: StateValue, previousValue: StateValue) => void;

// ─── Derived State ───

export interface DerivedStateDefinition {
  id: string;
  /** State keys this depends on */
  dependencies: string[];
  /** Pure compute function (receives dependency values, returns derived value) */
  compute: (deps: Record<string, StateValue>) => StateValue;
}

// ─── Async State ───

export type AsyncStatus = "idle" | "loading" | "success" | "error";

export interface AsyncStateValue {
  status: AsyncStatus;
  data: StateValue;
  error: string | null;
  timestamp: number;
}

// ─── App State Engine ───

class AppStateEngine {
  private definitions = new Map<string, StateDefinition>();
  private values = new Map<string, StateValue>();
  private listeners = new Map<string, Set<StateListener>>();
  private globalListeners = new Set<StateListener>();
  private derivedDefs = new Map<string, DerivedStateDefinition>();
  private initialized = false;

  /**
   * Register state definitions from schema.
   * Must be called before any get/set operations.
   */
  init(definitions: StateDefinition[]): void {
    this.definitions.clear();
    this.values.clear();

    for (const def of definitions) {
      const key = this.buildKey(def);
      this.definitions.set(key, def);
      this.values.set(key, structuredClone(def.initialValue));
    }

    this.initialized = true;
    logger.info("AppStateEngine initialized", { stateCount: definitions.length });
  }

  /**
   * Register a derived/computed state.
   */
  registerDerived(def: DerivedStateDefinition): void {
    this.derivedDefs.set(def.id, def);
    // Compute initial value
    this.recomputeDerived(def.id);
  }

  /**
   * Get a state value by key.
   */
  get(key: string): StateValue {
    // Check derived first
    if (this.derivedDefs.has(key)) {
      return this.recomputeDerived(key);
    }
    return this.values.get(key) ?? null;
  }

  /**
   * Set a state value. Validates type, notifies listeners, emits events.
   */
  set(key: string, value: StateValue, source: "user" | "action" | "system" = "user"): boolean {
    const def = this.definitions.get(key);
    if (!def) {
      logger.warn(`State key "${key}" not defined - ignoring set`);
      return false;
    }

    const previous = this.values.get(key) ?? null;

    // Type validation
    if (!this.validateType(value, def.type)) {
      logger.warn(`Type mismatch for "${key}": expected ${def.type}, got ${typeof value}`);
      return false;
    }

    this.values.set(key, value);

    // Notify listeners
    const keyListeners = this.listeners.get(key);
    if (keyListeners) {
      for (const fn of keyListeners) {
        try { fn(key, value, previous); } catch (e) {
          logger.error(`State listener error for "${key}"`, { error: e });
        }
      }
    }

    for (const fn of this.globalListeners) {
      try { fn(key, value, previous); } catch (e) {
        logger.error("Global state listener error", { error: e });
      }
    }

    // Recompute derived states that depend on this key
    for (const [derivedId, derivedDef] of this.derivedDefs) {
      if (derivedDef.dependencies.includes(key)) {
        this.recomputeDerived(derivedId);
      }
    }

    // Emit event
    eventBus.emit({ type: "state:changed", key });

    return true;
  }

  /**
   * Subscribe to changes on a specific key. Returns unsubscribe function.
   */
  subscribe(key: string, listener: StateListener): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(listener);
    return () => this.listeners.get(key)?.delete(listener);
  }

  /**
   * Subscribe to ALL state changes.
   */
  subscribeAll(listener: StateListener): () => void {
    this.globalListeners.add(listener);
    return () => this.globalListeners.delete(listener);
  }

  /**
   * Take a serializable snapshot of all state.
   */
  snapshot(): StateSnapshot {
    const global: Record<string, StateValue> = {};
    const pages: Record<string, Record<string, StateValue>> = {};
    const components: Record<string, Record<string, StateValue>> = {};

    for (const [key, def] of this.definitions) {
      const value = this.values.get(key) ?? null;
      if (def.scope === "global") {
        global[def.id] = value;
      } else if (def.scope === "page" && def.ownerId) {
        if (!pages[def.ownerId]) pages[def.ownerId] = {};
        pages[def.ownerId][def.id] = value;
      } else if (def.scope === "component" && def.ownerId) {
        if (!components[def.ownerId]) components[def.ownerId] = {};
        components[def.ownerId][def.id] = value;
      }
    }

    return { global, pages, components, capturedAt: Date.now() };
  }

  /**
   * Restore state from a snapshot.
   */
  restore(snapshot: StateSnapshot): void {
    for (const [id, value] of Object.entries(snapshot.global)) {
      const key = `global:${id}`;
      if (this.values.has(key)) this.values.set(key, value);
    }
    for (const [ownerId, states] of Object.entries(snapshot.pages)) {
      for (const [id, value] of Object.entries(states)) {
        const key = `page:${ownerId}:${id}`;
        if (this.values.has(key)) this.values.set(key, value);
      }
    }
    for (const [ownerId, states] of Object.entries(snapshot.components)) {
      for (const [id, value] of Object.entries(states)) {
        const key = `component:${ownerId}:${id}`;
        if (this.values.has(key)) this.values.set(key, value);
      }
    }
    logger.info("State restored from snapshot");
  }

  /**
   * Reset a specific scope to initial values.
   */
  resetScope(scope: StateScope, ownerId?: string): void {
    for (const [key, def] of this.definitions) {
      if (def.scope === scope && (!ownerId || def.ownerId === ownerId)) {
        this.values.set(key, structuredClone(def.initialValue));
      }
    }
    logger.info("State scope reset", { scope, ownerId });
  }

  /**
   * Reset ALL state to initial values.
   */
  resetAll(): void {
    for (const [key, def] of this.definitions) {
      this.values.set(key, structuredClone(def.initialValue));
    }
    logger.info("All state reset to initial values");
  }

  /**
   * Get all state definitions (for introspection/debugging).
   */
  getDefinitions(): StateDefinition[] {
    return [...this.definitions.values()];
  }

  /**
   * Set async state - manages loading/success/error lifecycle.
   */
  setAsync(key: string, status: AsyncStatus, data?: StateValue, error?: string): boolean {
    const asyncVal: AsyncStateValue = {
      status,
      data: data ?? null,
      error: error ?? null,
      timestamp: Date.now(),
    };
    return this.set(key, asyncVal as unknown as StateValue, "system");
  }

  /**
   * Get async state status.
   */
  getAsyncStatus(key: string): AsyncStatus {
    const val = this.get(key);
    if (val && typeof val === "object" && !Array.isArray(val) && "status" in (val as Record<string, unknown>)) {
      return (val as unknown as AsyncStateValue).status;
    }
    return "idle";
  }

  /**
   * Get all current values as a flat map.
   */
  getAllValues(): Record<string, StateValue> {
    const result: Record<string, StateValue> = {};
    for (const [key, value] of this.values) {
      result[key] = value;
    }
    return result;
  }

  /**
   * Teardown - clear everything.
   */
  teardown(): void {
    this.definitions.clear();
    this.values.clear();
    this.listeners.clear();
    this.globalListeners.clear();
    this.derivedDefs.clear();
    this.initialized = false;
  }

  // ─── Internals ───

  private buildKey(def: StateDefinition): string {
    if (def.scope === "global") return `global:${def.id}`;
    return `${def.scope}:${def.ownerId || "unknown"}:${def.id}`;
  }

  private validateType(value: StateValue, expectedType: string): boolean {
    if (value === null) return true; // null is valid for any type
    switch (expectedType) {
      case "string": return typeof value === "string";
      case "number": return typeof value === "number";
      case "boolean": return typeof value === "boolean";
      case "array": return Array.isArray(value);
      case "object": return typeof value === "object" && !Array.isArray(value);
      case "null": return value === null;
      default: return true;
    }
  }

  private recomputeDerived(id: string): StateValue {
    const def = this.derivedDefs.get(id);
    if (!def) return null;

    const deps: Record<string, StateValue> = {};
    for (const depKey of def.dependencies) {
      deps[depKey] = this.values.get(depKey) ?? null;
    }

    try {
      const value = def.compute(deps);
      this.values.set(id, value);
      return value;
    } catch (e) {
      logger.error(`Derived state "${id}" computation failed`, { error: e });
      return null;
    }
  }
}

export const appState = new AppStateEngine();
